import { lazy, type ComponentType, type LazyExoticComponent } from "react";

type Importer = () => Promise<{ default: ComponentType }>;

const CHUNK_ERROR_PATTERNS = [
  /loading chunk [^\s]+ failed/i,
  /loading css chunk [^\s]+ failed/i,
  /failed to fetch dynamically imported module/i,
  /error loading dynamically imported module/i,
  /importing a module script failed/i,
];

/**
 * Identifica falhas de carregamento de chunk JS/CSS (queda de rede, deploy
 * novo que invalidou o hash do chunk, ou stall do timeout abaixo). Usado pelo
 * RouteErrorBoundary para decidir o auto-reload.
 */
export function isChunkLoadError(error: unknown): boolean {
  if (!error) return false;
  const e = error as { name?: string; message?: string };
  if (e.name === "ChunkLoadError") return true;
  const message = e.message ?? "";
  return CHUNK_ERROR_PATTERNS.some((re) => re.test(message));
}

/**
 * Stall de chunk no browser (ex.: trava de HTTP/2 no edge) deixa o `import()`
 * pendente para sempre. Tratamos como ChunkLoadError para que o retry e, no
 * limite, o auto-reload do boundary recuperem a página.
 */
class ChunkTimeoutError extends Error {
  constructor() {
    super("Loading chunk timed out");
    this.name = "ChunkLoadError";
  }
}

function withTimeout<T>(promise: Promise<T>, ms: number): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    const timer = setTimeout(() => reject(new ChunkTimeoutError()), ms);
    promise.then(
      (value) => {
        clearTimeout(timer);
        resolve(value);
      },
      (err) => {
        clearTimeout(timer);
        reject(err);
      },
    );
  });
}

const delay = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

type RetryOptions = { retries?: number; timeoutMs?: number; baseDelayMs?: number };

/**
 * Carrega o módulo com timeout (stall → erro) e retry com backoff (cobre falhas
 * transitórias de rede, em que o `import()` rejeita e o módulo sai do registry,
 * permitindo nova busca). O que sobreviver às tentativas é relançado.
 */
export async function loadWithRetry(
  importer: Importer,
  { retries = 1, timeoutMs = 10_000, baseDelayMs = 400 }: RetryOptions = {},
): Promise<{ default: ComponentType }> {
  let lastError: unknown;
  for (let attempt = 0; attempt <= retries; attempt++) {
    try {
      return await withTimeout(importer(), timeoutMs);
    } catch (err) {
      lastError = err;
      if (attempt < retries) await delay(baseDelayMs * 2 ** attempt);
    }
  }
  throw lastError;
}

/**
 * `React.lazy` resiliente: o que sobreviver ao timeout+retry vira ChunkLoadError,
 * tratado pelo RouteErrorBoundary (auto-reload), em vez de travar no Suspense.
 */
export function lazyWithRetry(
  importer: Importer,
  options?: RetryOptions,
): LazyExoticComponent<ComponentType> {
  return lazy(() => loadWithRetry(importer, options));
}
